import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Alert,
    Snackbar,
    Chip,
    CircularProgress,
    Tooltip,
    Grid,
    Card,
    CardContent,
    CardActions,
    LinearProgress,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    AdminPanelSettings as AdminIcon,
    Person as UserIcon,
    CheckCircle as ActiveIcon,
    Cancel as DisabledIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { userService } from '../services/api';
import authService from '../services/auth';
import { formatLocalDateTime } from '../utils/date';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [processing, setProcessing] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({ is_valid: false, errors: [] });
    const [usernameValidation, setUsernameValidation] = useState({ is_valid: false, errors: [] });

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        password_confirm: '',
        role: 'user',
        account_status: 'active'
    });

    const currentUser = authService.getStoredUser();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await userService.getUsers();
            setUsers(data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch users');
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                username: user.username,
                email: user.email || '',
                password: '',
                password_confirm: '',
                role: user.role,
                account_status: user.account_status
            });
        } else {
            setEditingUser(null);
            setFormData({
                username: '',
                email: '',
                password: '',
                password_confirm: '',
                role: 'user',
                account_status: 'active'
            });
        }
        setDialogOpen(true);
        setPasswordValidation({ is_valid: false, errors: [] });

        if (user) {
            // When editing, pre-filled username is already valid — set immediately to avoid race condition
            setUsernameValidation({ is_valid: true, errors: [] });
        } else {
            setUsernameValidation({ is_valid: false, errors: [] });
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingUser(null);
        setFormData({
            username: '',
            email: '',
            password: '',
            password_confirm: '',
            role: 'user',
            account_status: 'active'
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate password in real-time
        if (field === 'password' && value) {
            validatePassword(value);
        }

        // Validate username in real-time
        if (field === 'username' && value) {
            validateUsername(value);
        }
    };

    const validatePassword = async (password) => {
        try {
            const validation = await userService.validatePassword(password);
            setPasswordValidation(validation);
        } catch (err) {
            setPasswordValidation({ is_valid: false, errors: ['Failed to validate password'] });
        }
    };

    const validateUsername = async (username) => {
        try {
            const validation = await userService.validateUsername(username);
            setUsernameValidation(validation);
        } catch (err) {
            setUsernameValidation({ is_valid: false, errors: ['Failed to validate username'] });
        }
    };

    const handleSubmit = async () => {
        try {
            setProcessing(true);

            // Validate form
            const passwordValue = formData.password?.trim();
            const passwordConfirmValue = formData.password_confirm?.trim();

            if (!formData.username.trim()) {
                throw new Error('Username is required');
            }

            if (!editingUser && !passwordValue) {
                throw new Error('Password is required for new users');
            }

            if (passwordValue && passwordValue !== passwordConfirmValue) {
                throw new Error('Passwords do not match');
            }

            if (passwordValue && !passwordValidation.is_valid) {
                throw new Error('Password does not meet requirements');
            }

            if (!usernameValidation.is_valid) {
                throw new Error('Username does not meet requirements');
            }

            // Prepare data for API
            const userData = {
                username: formData.username.trim(),
                email: formData.email.trim() || null,
                role: formData.role,
                account_status: formData.account_status
            };

            if (passwordValue) {
                userData.password = passwordValue;
            }

            if (editingUser) {
                await userService.updateUser(editingUser.id, userData);
                setSnackbar({
                    open: true,
                    message: 'User updated successfully',
                    severity: 'success'
                });
            } else {
                await userService.createUser(userData);
                setSnackbar({
                    open: true,
                    message: 'User created successfully',
                    severity: 'success'
                });
            }

            handleCloseDialog();
            fetchUsers();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.detail || err.message || 'Failed to save user',
                severity: 'error'
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteClick = (user) => {
        if (user.id === currentUser?.id) {
            setSnackbar({
                open: true,
                message: 'Cannot delete your own account',
                severity: 'error'
            });
            return;
        }
        setUserToDelete(user);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setProcessing(true);
            await userService.deleteUser(userToDelete.id);
            setSnackbar({
                open: true,
                message: 'User deleted successfully',
                severity: 'success'
            });
            setDeleteConfirmOpen(false);
            setUserToDelete(null);
            fetchUsers();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.detail || 'Failed to delete user',
                severity: 'error'
            });
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'disabled':
                return 'warning';
            case 'deleted':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return <ActiveIcon />;
            case 'disabled':
                return <DisabledIcon />;
            case 'deleted':
                return <WarningIcon />;
            default:
                return <WarningIcon />;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return formatLocalDateTime(dateString);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h1">
                        User Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        disabled={processing}
                    >
                        Add User
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Username</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Last Login</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {user.role === 'admin' ? <AdminIcon color="primary" /> : <UserIcon />}
                                            <Typography variant="body2" fontWeight="medium">
                                                {user.username}
                                            </Typography>
                                            {user.id === currentUser?.id && (
                                                <Chip label="You" size="small" color="primary" variant="outlined" />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{user.email || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={user.role === 'user' ? 'Auditor' : user.role}
                                            color={user.role === 'admin' ? 'primary' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getStatusIcon(user.account_status)}
                                            label={user.account_status}
                                            color={getStatusColor(user.account_status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(user.created_at)}</TableCell>
                                    <TableCell>{formatDate(user.last_login)}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit User">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenDialog(user)}
                                                    disabled={processing}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={user.id === currentUser?.id ? "Cannot delete your own account" : "Delete User"}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(user)}
                                                    disabled={processing || user.id === currentUser?.id}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Add/Edit User Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingUser ? 'Edit User' : 'Add New User'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Username"
                            value={formData.username}
                            onChange={(e) => handleInputChange('username', e.target.value)}
                            fullWidth
                            required
                            disabled={processing}
                            autoComplete="off"
                            error={formData.username && !usernameValidation.is_valid}
                            helperText={
                                formData.username && !usernameValidation.is_valid
                                    ? usernameValidation.errors.join(', ')
                                    : ''
                            }
                        />

                        <TextField
                            label="Email (optional)"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            fullWidth
                            disabled={processing}
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={formData.role}
                                onChange={(e) => handleInputChange('role', e.target.value)}
                                label="Role"
                                disabled={processing}
                            >
                                <MenuItem value="user">Auditor</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth required>
                            <InputLabel>Account Status</InputLabel>
                            <Select
                                value={formData.account_status}
                                onChange={(e) => handleInputChange('account_status', e.target.value)}
                                label="Account Status"
                                disabled={processing}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="disabled">Disabled</MenuItem>
                                <MenuItem value="deleted">Deleted</MenuItem>
                            </Select>
                        </FormControl>

                        {!editingUser && (
                            <>
                                <TextField
                                    label="Password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    fullWidth
                                    required
                                    disabled={processing}
                                    error={formData.password && !passwordValidation.is_valid}
                                    helperText={
                                        formData.password && !passwordValidation.is_valid
                                            ? passwordValidation.errors.join(', ')
                                            : ''
                                    }
                                />

                                <TextField
                                    label="Confirm Password"
                                    type="password"
                                    value={formData.password_confirm}
                                    onChange={(e) => handleInputChange('password_confirm', e.target.value)}
                                    fullWidth
                                    required
                                    disabled={processing}
                                    error={formData.password_confirm && formData.password !== formData.password_confirm}
                                    helperText={
                                        formData.password_confirm && formData.password !== formData.password_confirm
                                            ? 'Passwords do not match'
                                            : ''
                                    }
                                />
                            </>
                        )}

                        {editingUser && (
                            <>
                                <Divider />
                                <Typography variant="subtitle2" color="text.secondary">
                                    Change Password (leave empty to keep current password)
                                </Typography>
                                <TextField
                                    label="New Password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    fullWidth
                                    disabled={processing}
                                    autoComplete="new-password"
                                    error={formData.password && !passwordValidation.is_valid}
                                    helperText={
                                        formData.password && !passwordValidation.is_valid
                                            ? passwordValidation.errors.join(', ')
                                            : ''
                                    }
                                />

                                <TextField
                                    label="Confirm New Password"
                                    type="password"
                                    value={formData.password_confirm}
                                    onChange={(e) => handleInputChange('password_confirm', e.target.value)}
                                    fullWidth
                                    disabled={processing}
                                    autoComplete="new-password"
                                    error={formData.password_confirm && formData.password !== formData.password_confirm}
                                    helperText={
                                        formData.password_confirm && formData.password !== formData.password_confirm
                                            ? 'Passwords do not match'
                                            : ''
                                    }
                                />
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={processing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={processing}
                        startIcon={processing ? <CircularProgress size={20} /> : null}
                    >
                        {processing ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the user "{userToDelete?.username}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} disabled={processing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={processing}
                        startIcon={processing ? <CircularProgress size={20} /> : null}
                    >
                        {processing ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserManagement; 